import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, BRAND } from "../../lib/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "glass-nav py-3" : "py-5 bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group shrink-0">
          <span className="font-display font-black text-lg tracking-tight text-white">SPECA</span>
          <span className="w-1 h-1 bg-silver rounded-full group-hover:scale-150 transition-transform" />
          <span className="font-mono-tech text-[10px] tracking-[0.3em] text-gray-500 hidden sm:inline">DETAILING</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-sm transition-colors font-medium tracking-wide ${isActive ? "text-white" : "text-gray-400 hover:text-white"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <a href={`tel:${BRAND.phoneRaw}`} data-testid="nav-phone" className="hidden md:flex items-center gap-2 text-xs text-gray-400 hover:text-white transition-colors font-mono-tech">
            <Phone size={13} /> {BRAND.phone}
          </a>
          <Link to="/contact" data-testid="nav-book-now" className="hidden sm:inline-block bg-silver hover:bg-white text-black px-5 py-2.5 transition-colors uppercase tracking-widest text-xs font-bold">
            Book Now
          </Link>
          <button data-testid="nav-menu-toggle" onClick={() => setOpen((v) => !v)} className="xl:hidden text-white p-2" aria-label="Toggle menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="xl:hidden glass-nav overflow-hidden mt-3"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`mobile-nav-link-${l.to.replace("/", "") || "home"}`}
                  className="text-left py-3 text-gray-300 hover:text-white border-b border-white/5 text-sm tracking-wide">
                  {l.label}
                </NavLink>
              ))}
              <Link to="/contact" data-testid="mobile-book-now" className="mt-4 bg-silver text-black py-3 uppercase tracking-widest text-xs font-bold text-center">
                Book Now
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
